"use client";

import type { RazorpayCheckoutOptions } from "@/lib/razorpay-checkout";

type BuildRazorpayUpiIntentCheckoutOptionsInput = {
  key: string;
  amountMinor: number;
  currency: string;
  merchantName: string;
  description: string;
  orderId: string;
  callbackUrl?: string;
  nativeWebView: boolean;
  /** Redirect only when the native shell granted the off-origin navigation lease. */
  redirectToCallback: boolean;
  payer: {
    name?: string;
    email?: string;
    contact?: string;
  };
  customerId?: string;
  notes?: Record<string, string>;
  themeColor: string;
};

/**
 * Build an Intent-first UPI checkout that still behaves correctly off mobile.
 *
 * Razorpay chooses the final surface:
 * - mobile web: installed UPI app handoff;
 * - Capacitor WebView: app handoff enabled by `webview_intent`;
 * - desktop web: dynamic UPI QR inside Standard Checkout.
 *
 * There is deliberately no VPA here. Supplying one turns this back into the
 * Collect flow, which remains a separate, explicit option in Hiive's picker.
 */
export const buildRazorpayUpiIntentCheckoutOptions = ({
  key,
  amountMinor,
  currency,
  merchantName,
  description,
  orderId,
  callbackUrl,
  nativeWebView,
  redirectToCallback,
  payer,
  customerId,
  notes,
  themeColor,
}: BuildRazorpayUpiIntentCheckoutOptionsInput): RazorpayCheckoutOptions => {
  const useCallback = redirectToCallback && Boolean(callbackUrl);

  return {
    key,
    amount: amountMinor,
    currency,
    name: merchantName,
    description,
    order_id: orderId,
    ...(useCallback && callbackUrl ? { callback_url: callbackUrl } : {}),
    redirect: useCallback,
    ...(nativeWebView ? { webview_intent: true } : {}),
    ...(customerId ? { customer_id: customerId } : {}),
    prefill: {
      ...payer,
      method: "upi",
    },
    /**
     * No provider-side retry, deliberately.
     *
     * Razorpay's retry screen is what the "Unable to open UPI app" dead end
     * actually is — and because this block strips every other method out of the
     * modal (`show_default_blocks: false`), the only thing it can offer to retry
     * is the app that just failed to open. Worse, leaving it enabled meant
     * `payment.failed` never settled the checkout, so the real cause
     * (`intent_no_apps_error`) was never read at all.
     *
     * Off, the modal closes on failure and recovery happens in Hiive's own
     * sheet, which has the QR, cards and a UPI ID in it.
     */
    retry: { enabled: false },
    config: {
      display: {
        blocks: {
          upi: {
            name: "UPI Apps & QR",
            instruments: [{ method: "upi" }],
          },
        },
        sequence: ["block.upi"],
        preferences: {
          show_default_blocks: false,
        },
      },
    },
    notes,
    theme: { color: themeColor },
  };
};
